import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Button, TextField, CircularProgress, Alert, Divider } from '@mui/material';
import { AxiosError } from 'axios';
import ApiService from '../services/api';

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as AxiosError<{ detail?: unknown }>;
  const d = ax.response?.data?.detail;
  if (typeof d === 'string' && d.trim()) return d;
  if (Array.isArray(d)) {
    const parts = d.map((item) =>
      typeof item === 'object' && item !== null && 'msg' in item
        ? String((item as { msg: unknown }).msg)
        : JSON.stringify(item),
    );
    const joined = parts.join('; ').trim();
    if (joined) return joined;
  }
  if (d !== undefined && d !== null && typeof d === 'object') {
    try {
      return JSON.stringify(d);
    } catch {
      /* ignore */
    }
  }
  const status = ax.response?.status;
  if (ax.message) return status ? `${ax.message} (${status})` : ax.message;
  if (status) return `Request failed (${status})`;
  return fallback;
}

interface OrderingApprovalEmailTabProps {
  onError?: (msg: string) => void;
}

const PLACEHOLDER_HELP = [
  '{{approval_url}}',
  '{{customer_name}}',
  '{{order_reference}}',
  '{{customer_email}}',
  '{{service_address}}',
  '{{organisation_name}}',
].join(' · ');

const OrderingApprovalEmailTab: React.FC<OrderingApprovalEmailTabProps> = ({ onError }) => {
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBodyHtml, setEmailBodyHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLocalError(null);
      const data = await ApiService.adminGetOrderApprovalEmail();
      setEmailSubject(data.email_subject ?? '');
      setEmailBodyHtml(data.email_body_html ?? '');
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, 'Failed to load order approval email template');
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccess(null);
      setLocalError(null);
      const data = await ApiService.adminPutOrderApprovalEmail({
        email_subject: emailSubject,
        email_body_html: emailBodyHtml,
      });
      setEmailSubject(data.email_subject ?? '');
      setEmailBodyHtml(data.email_body_html ?? '');
      setSuccess('Saved successfully.');
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, 'Failed to save order approval email template');
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Order approval email
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When staff choose &quot;Send approval email&quot; after generating a link, this message is sent to the
        customer&apos;s order email via Mailgun. The link uses your customer portal base URL from the Customer
        approval tab (not the SonOps staff hostname).
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Email subject"
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
        sx={{ mb: 2 }}
        helperText="Supports {{placeholders}} (e.g. {{order_reference}})."
      />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Email content (HTML)
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {PLACEHOLDER_HELP}
      </Typography>

      <TextField
        fullWidth
        multiline
        minRows={14}
        label="HTML body"
        value={emailBodyHtml}
        onChange={(e) => setEmailBodyHtml(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.8125rem', alignItems: 'flex-start' } }}
      />

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
    </Paper>
  );
};

export default OrderingApprovalEmailTab;
